import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Loader2, Check, ArrowRight, Copy, Download, ExternalLink, Plus, X, GripVertical, Play, ArrowDown, ArrowUp, ListOrdered } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const COLORS = {
  blue: "#3B4FE4",
  blueLight: "#EEF0FD",
  amber: "#D4860A",
  amberLight: "#FFF4E0",
  green: "#1A7A4A",
  greenLight: "#E6F5ED",
  bg2: "#F5F6FA",
  bg3: "#EEEEF5",
  text: "#111827",
  text2: "#4B5563",
  text3: "#9CA3AF",
  border: "rgba(0,0,0,0.08)",
};

const PRIORITY_BADGE = {
  1: { bg: COLORS.blue, color: "white" },
  2: { bg: COLORS.amberLight, color: COLORS.amber },
  3: { bg: COLORS.greenLight, color: COLORS.green },
};

const STATUS_PILL = {
  not_started: { label: "Not Started", bg: COLORS.bg3, color: COLORS.text2 },
  in_setup: { label: "In Setup", bg: COLORS.amberLight, color: COLORS.amber },
  complete: { label: "Complete", bg: COLORS.greenLight, color: COLORS.green },
};

const headers = (tenantId) => ({
  "Content-Type": "application/json",
  "x-tenant-id": tenantId || "tenant_1",
  "x-user-id": "client",
  "x-user-role": "client",
});

// ──────────────────────── Overview ────────────────────────

export function PlaybookOverview({ tenantId, prospectId, onOpenPlaybook }) {
  const [playbooks, setPlaybooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeIdx, setActiveIdx] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = prospectId ? `?prospect_id=${prospectId}` : "";
      const r = await fetch(`${BACKEND_URL}/api/playbooks${params}`, { headers: headers(tenantId) });
      const data = await r.json();
      setPlaybooks(Array.isArray(data) ? data : []);
    } catch {
      toast.error("Could not load playbooks");
    } finally {
      setLoading(false);
    }
  }, [tenantId, prospectId]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#9CA3AF]" /></div>;

  if (playbooks.length === 0) {
    return (
      <div className="rounded-xl border border-[#e5e7eb] bg-white p-10 text-center" data-testid="playbooks-empty">
        <div className="text-[16px] font-semibold text-[#111827] mb-2">No Playbooks Yet</div>
        <p className="text-[14px] text-[#4B5563]">
          Confirm your workflow selection in <strong>Workflows Targeted</strong> to auto-create playbooks.
        </p>
      </div>
    );
  }

  const active = playbooks[activeIdx] || playbooks[0];

  return (
    <div data-testid="playbook-overview">
      <div className="mb-6 flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-[22px] font-semibold tracking-tight text-[#111827]">Playbooks</h1>
          <p className="mt-1 text-[14px] text-[#4B5563]">{playbooks.length} workflows identified from your assessment</p>
        </div>
      </div>

      <div className="text-[10px] font-semibold tracking-[0.10em] uppercase text-[#9CA3AF] mb-3">Your Top {playbooks.length} Workflows</div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        {playbooks.map((pb, idx) => {
          const isActive = idx === activeIdx;
          const pBadge = PRIORITY_BADGE[pb.priority] || PRIORITY_BADGE[3];
          const sPill = STATUS_PILL[pb.status] || STATUS_PILL.not_started;
          return (
            <button
              key={pb.id}
              type="button"
              onClick={() => setActiveIdx(idx)}
              className="text-left rounded-xl border bg-white p-5 transition-all hover:shadow-sm"
              style={{ borderColor: isActive ? COLORS.blue : COLORS.border, borderWidth: isActive ? "2px" : "1px" }}
              data-testid={`playbook-card-${pb.id}`}
            >
              <div className="flex items-start justify-between gap-2 mb-2">
                <span className="px-2 py-0.5 text-[11px] font-bold rounded" style={{ backgroundColor: pBadge.bg, color: pBadge.color }}>
                  {pb.priority}
                </span>
                <span className="px-2 py-0.5 text-[11px] font-semibold rounded-full" style={{ backgroundColor: sPill.bg, color: sPill.color }}>
                  {sPill.label}
                </span>
              </div>
              <div className="text-[10px] font-semibold uppercase tracking-[0.10em] text-[#9CA3AF]">Workflow {String(idx + 1).padStart(2, "0")}</div>
              <div className="text-[15px] font-semibold text-[#111827] mt-1">{pb.workflow_name}</div>
              <p className="text-[13px] text-[#4B5563] mt-2 line-clamp-2 leading-relaxed">{pb.trigger?.description || ""}</p>
              <div className="mt-3 pt-3 border-t border-[rgba(0,0,0,0.06)] flex items-center justify-between">
                <span className="text-[11px] text-[#4B5563]">Progress: <strong className="text-[#111827]">{pb.progress || 0}%</strong></span>
                <span className="text-[11px] text-[#9CA3AF]">{pb.progress >= 100 ? "Complete" : pb.progress > 0 ? "In setup" : "Not started"}</span>
              </div>
            </button>
          );
        })}
      </div>

      {/* Detail panel for selected card */}
      {active && <TISOPanel playbook={active} onContinue={() => onOpenPlaybook?.(active.id)} />}
    </div>
  );
}

// ──────── TISO Panel (under cards) ────────

const TISOPanel = ({ playbook, onContinue }) => {
  const sections = [
    { key: "trigger", icon: Play, label: "Trigger", lines: ["What starts this workflow", "Trigger source", "Frequency & volume"] },
    { key: "inputs", icon: ArrowDown, label: "Inputs", lines: ["Systems & integrations", "RAG documents", "Enrichment sources"] },
    { key: "steps", icon: ListOrdered, label: "Steps", lines: ["End-to-end steps defined", "Guardrails & review rules", "Escalation logic"] },
    { key: "outputs", icon: ArrowUp, label: "Outputs", lines: ["Expected deliverables", "Success metrics", "AI tool config export"] },
  ];
  const sStatus = (key) => {
    const s = playbook[key] || {};
    if (s.completed) return { label: "✓ Complete", color: COLORS.green };
    const filled = key === "trigger" ? !!s.description :
                   key === "inputs" ? (s.integrations?.length > 0 || s.rag_documents?.length > 0) :
                   key === "steps" ? (s.workflow_steps?.length > 0) :
                   (s.expected_outputs?.length > 0 || s.kpis?.length > 0);
    return filled ? { label: "In Progress", color: COLORS.amber } : { label: "Not started", color: COLORS.text3 };
  };

  return (
    <div className="rounded-xl border border-[rgba(0,0,0,0.08)] bg-white p-6" data-testid="tiso-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] font-semibold tracking-[0.10em] uppercase text-[#9CA3AF]">Workflow {String(playbook.priority).padStart(2, "0")} — Setup Progress</div>
          <h2 className="text-[18px] font-semibold text-[#111827] mt-1">{playbook.workflow_name}</h2>
          <p className="text-[13px] text-[#4B5563] mt-1">Complete all 4 sections below to generate your AI agent config</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <div className="text-[24px] font-bold text-[#111827] leading-none">{playbook.progress || 0}%</div>
            <div className="text-[11px] text-[#9CA3AF]">progress</div>
          </div>
          <span className="px-3 py-1 text-[11px] font-semibold rounded-full" style={{ backgroundColor: COLORS.amberLight, color: COLORS.amber }}>
            {STATUS_PILL[playbook.status]?.label || "In Setup"}
          </span>
        </div>
      </div>

      <div className="mt-4 h-2 rounded-full bg-[#EEEEF5] overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${playbook.progress || 0}%`, backgroundColor: COLORS.blue }} />
      </div>

      <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-3">
        {sections.map((s) => {
          const Icon = s.icon;
          const st = sStatus(s.key);
          return (
            <div key={s.key} className="rounded-lg border p-4" style={{ borderColor: COLORS.border, backgroundColor: COLORS.bg2 }}>
              <div className="flex items-center gap-2 mb-2">
                <Icon className="h-4 w-4 text-[#3B4FE4]" />
                <div className="text-[14px] font-semibold text-[#111827]">{s.label}</div>
              </div>
              <div className="text-[11px] font-bold mb-2" style={{ color: st.color }}>{st.label}</div>
              <ul className="space-y-1">
                {s.lines.map((line, i) => (
                  <li key={i} className="text-[12px] text-[#4B5563] leading-snug">• {line}</li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>

      <div className="mt-6 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[11px] font-semibold uppercase text-[#9CA3AF] tracking-wider">Connected tools:</span>
          {(playbook.inputs?.integrations || []).filter((i) => i.enabled).map((i, idx) => (
            <span key={idx} className="px-2.5 py-1 text-[11px] rounded-full" style={{ backgroundColor: COLORS.blueLight, color: COLORS.blue }}>
              {i.name}
            </span>
          ))}
          {(playbook.inputs?.integrations || []).filter((i) => i.enabled).length === 0 && (
            <span className="text-[12px] text-[#9CA3AF]">none yet</span>
          )}
        </div>
        <button
          type="button"
          onClick={onContinue}
          className="inline-flex items-center gap-2 px-5 h-10 rounded-lg text-[13px] font-bold text-white"
          style={{ backgroundColor: COLORS.blue, boxShadow: "0 4px 12px rgba(59,79,228,0.30)" }}
          data-testid="continue-setup-btn"
        >
          Continue Setup <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

// ──────────────────────── Detail (4 tabs) ────────────────────────

const TRIGGER_SOURCES = ["Website form", "Inbound email", "CRM event", "Scheduled", "Webhook", "Manual"];
const ENRICH_SOURCES = ["LinkedIn (via API)", "Apollo.io", "Clearbit", "ZoomInfo", "None"];
const CRM_SOURCES = ["Salesforce — Read only", "HubSpot — Read only", "Salesforce — Read + Write", "No CRM access"];
const REVIEW_OPTIONS = ["Sending any email", "Booking a meeting", "Updating deal stage", "Creating a contact", "Financial transaction", "External communication"];
const ESCALATE_OPTIONS = ["Legal / contract mention", "Pricing negotiation", "Competitor comparison", "Complaint or frustration", "Regulatory reference", "Data privacy question"];
const SLA_OPTIONS = [
  { label: "Under 5 minutes", value: 5 },
  { label: "Under 30 minutes", value: 30 },
  { label: "Within 2 hours", value: 120 },
  { label: "Next business day", value: 1440 },
];
const ROUTE_TO_OPTIONS = ["Sales rep (by territory)", "Account Executive", "SDR queue", "Sales Manager"];

const PRESET_INTEGRATIONS = [
  { id: "salesforce", name: "Salesforce CRM", role: "Lead records, pipeline", options: ["Update lead status", "Log activity", "Create contacts", "Update deal stage"] },
  { id: "gmail", name: "Gmail / Outlook", role: "Draft & send follow-ups", options: ["Draft for review", "Auto-send approved templates", "Send without review"] },
  { id: "calendly", name: "Calendly", role: "Meeting booking", options: ["Read availability", "Book meeting"] },
  { id: "hubspot", name: "HubSpot", role: "Alt CRM", options: ["Sync contacts", "Update pipeline"] },
  { id: "slack", name: "Slack / Teams", role: "Notifications", options: ["Post to channel", "Send DM"] },
  { id: "zapier", name: "Zapier / Make", role: "Workflow bridge", options: ["Trigger zap", "Pass data"] },
];

const PRESET_STEPS = {
  Sales: [
    { description: "New lead inquiry arrives via website form or inbound email", type: "AI automated" },
    { description: "AI enriches lead with company data from enrichment source and CRM", type: "AI automated" },
    { description: "AI scores lead against ICP criteria and assigns priority tier", type: "AI automated" },
    { description: "AI drafts personalized response email using templates and RAG docs", type: "AI automated" },
    { description: "Sales rep reviews, edits if needed, and approves email for sending", type: "Human review" },
    { description: "AI logs activity in CRM, creates follow-up task, notifies team", type: "AI automated" },
    { description: "If no response in 48 hours, AI drafts follow-up for rep review", type: "Human review" },
  ],
  Operations: [
    { description: "Inbound request received via email, form, or queue", type: "AI automated" },
    { description: "AI classifies request type and urgency", type: "AI automated" },
    { description: "AI routes to correct team or individual based on rules", type: "AI automated" },
    { description: "Assignee receives summary and suggested response", type: "AI + human" },
    { description: "Human reviews and confirms or adjusts routing", type: "Human review" },
    { description: "AI logs ticket, updates status, sends acknowledgment", type: "AI automated" },
  ],
  Finance: [
    { description: "Period-end data becomes available in ERP/system", type: "AI automated" },
    { description: "AI identifies variances, exceptions, and anomalies", type: "AI automated" },
    { description: "AI drafts variance commentary and management narrative", type: "AI automated" },
    { description: "Finance team reviews and edits commentary", type: "Human review" },
    { description: "AI distributes approved report to leadership", type: "AI + human" },
    { description: "AI drafts AR collection follow-ups for overdue accounts", type: "AI automated" },
  ],
};

const PRESET_OUTPUTS = {
  Sales: [
    { name: "Personalized follow-up email — drafted & ready for rep review", description: "Tailored to industry, company size, and inquiry. Built from approved templates and RAG docs." },
    { name: "CRM record updated — lead scored, activity logged, next task created", description: "Record enriched, priority tier assigned, follow-up task queued." },
    { name: "Slack alert sent to sales channel for hot leads", description: "Triggered when lead score exceeds threshold. Includes summary + draft email link." },
  ],
  Operations: [
    { name: "Classified and routed request — assignee notified", description: "Request type, urgency, and routing decision documented and confirmed." },
    { name: "Acknowledgment sent to requestor", description: "Auto-drafted acknowledgment confirming receipt and expected response time." },
    { name: "Ticket created in workflow system", description: "Logged with classification, priority, and assignee populated." },
  ],
  Finance: [
    { name: "Variance commentary report — drafted for finance review", description: "Period-over-period changes explained, anomalies flagged, ready for management." },
    { name: "AR collection emails — drafted per overdue account", description: "Prioritized by days outstanding, personalized per account history." },
    { name: "Exception alert to CFO/controller for items above threshold", description: "Triggered for any exception exceeding the materiality threshold." },
  ],
};

const PRESET_KPIS = {
  Sales: [
    { name: "Lead response time", baseline: "", target: "< 30 minutes", unit: "from trigger" },
    { name: "Hours saved per rep", baseline: "", target: "4–6 hrs", unit: "per week" },
    { name: "CRM record completeness", baseline: "", target: "95%+", unit: "% complete" },
    { name: "Follow-up completion rate", baseline: "", target: "100%", unit: "hot leads/24h" },
  ],
  Operations: [
    { name: "Triage time per request", baseline: "", target: "< 5 minutes", unit: "from receipt" },
    { name: "Routing accuracy", baseline: "", target: "95%+", unit: "% correct" },
    { name: "Admin hours saved", baseline: "", target: "3–5 hrs", unit: "per week" },
  ],
  Finance: [
    { name: "Reporting cycle time", baseline: "", target: "2 days faster", unit: "vs current" },
    { name: "AR follow-up coverage", baseline: "", target: "100%", unit: "overdue accts" },
    { name: "Finance admin hours saved", baseline: "", target: "4–6 hrs", unit: "per period" },
  ],
};

const AI_TOOLS = [
  { id: "claude_cowork", label: "Claude Cowork", description: "Desktop agent with file access, long-context reasoning, and agentic workflow support.", badge: "Recommended" },
  { id: "openclaw", label: "OpenClaw", description: "Chat-first, self-hosted agent with multi-channel orchestration and action pipelines.", badge: "Action-first" },
  { id: "perplexity", label: "Perplexity / API", description: "Live web + RAG hybrid. Best for research-heavy or enrichment-dependent workflows.", badge: "Research" },
];

export function PlaybookDetail({ tenantId, playbookId, onBack }) {
  const [playbook, setPlaybook] = useState(null);
  const [tab, setTab] = useState("trigger");
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [dirty, setDirty] = useState(false);
  const draftRef = useRef({});

  const load = useCallback(async () => {
    if (!playbookId) return;
    try {
      const r = await fetch(`${BACKEND_URL}/api/playbooks/${playbookId}`, { headers: headers(tenantId) });
      if (!r.ok) throw new Error("not found");
      const data = await r.json();
      // Pre-populate steps/outputs/kpis if empty using category presets
      const cat = data.workflow_category || "Sales";
      if (!data.steps?.workflow_steps?.length && PRESET_STEPS[cat]) {
        data.steps = data.steps || {};
        data.steps.workflow_steps = PRESET_STEPS[cat].map((s, i) => ({ ...s, order: i + 1 }));
      }
      if (!data.outputs?.expected_outputs?.length && PRESET_OUTPUTS[cat]) {
        data.outputs = data.outputs || {};
        data.outputs.expected_outputs = PRESET_OUTPUTS[cat];
      }
      if (!data.outputs?.kpis?.length && PRESET_KPIS[cat]) {
        data.outputs.kpis = PRESET_KPIS[cat];
      }
      if (!data.inputs?.integrations?.length) {
        data.inputs = data.inputs || {};
        data.inputs.integrations = PRESET_INTEGRATIONS.map((i) => ({ ...i, enabled: false, write_permissions: [] }));
      }
      setPlaybook(data);
      draftRef.current = data;
    } catch {
      toast.error("Could not load playbook");
    }
  }, [playbookId, tenantId]);

  useEffect(() => { load(); }, [load]);

  const update = (section, patch) => {
    setPlaybook((prev) => {
      const next = { ...prev, [section]: { ...prev[section], ...patch } };
      draftRef.current = next;
      return next;
    });
    setDirty(true);
  };

  const setAiTool = (tool) => {
    setPlaybook((prev) => {
      const next = { ...prev, ai_tool: tool };
      draftRef.current = next;
      return next;
    });
    setDirty(true);
  };

  const persist = useCallback(async (markComplete) => {
    const pb = draftRef.current;
    if (!pb?.id) return;
    setSaving(true);
    try {
      const body = {
        trigger: pb.trigger,
        inputs: pb.inputs,
        steps: pb.steps,
        outputs: pb.outputs,
        ai_tool: pb.ai_tool,
      };
      if (markComplete) {
        body[markComplete] = { ...pb[markComplete], completed: true };
      }
      const r = await fetch(`${BACKEND_URL}/api/playbooks/${pb.id}`, {
        method: "PATCH",
        headers: headers(tenantId),
        body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error("save failed");
      const updated = await r.json();
      setPlaybook(updated);
      draftRef.current = updated;
      setLastSaved(new Date());
      setDirty(false);
    } catch {
      toast.error("Auto-save failed");
    } finally {
      setSaving(false);
    }
  }, [tenantId]);

  // Auto-save every 60s when dirty
  useEffect(() => {
    if (!dirty) return;
    const t = setTimeout(() => persist(), 60000);
    return () => clearTimeout(t);
  }, [dirty, persist]);

  if (!playbook) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#9CA3AF]" /></div>;

  const tabs = [
    { key: "trigger", label: "Trigger" },
    { key: "inputs", label: "Inputs" },
    { key: "steps", label: "Steps" },
    { key: "outputs", label: "Outputs" },
  ];

  return (
    <div className="pb-20" data-testid="playbook-detail">
      <button
        type="button"
        onClick={onBack}
        className="text-[12px] font-medium text-[#4B5563] hover:text-[#111827] mb-3 inline-flex items-center gap-1"
        data-testid="playbook-back-btn"
      >
        ← Back to Playbooks
      </button>
      <div className="rounded-xl bg-white border border-[rgba(0,0,0,0.08)] p-5 mb-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[10px] font-semibold tracking-[0.10em] uppercase text-[#9CA3AF]">Workflow {String(playbook.priority).padStart(2, "0")}</div>
            <h1 className="text-[20px] font-semibold text-[#111827] mt-0.5">{playbook.workflow_name}</h1>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <div className="text-[20px] font-bold" style={{ color: COLORS.blue }}>{playbook.progress || 0}%</div>
            </div>
            <span className="px-3 py-1 text-[11px] font-semibold rounded-full" style={{ backgroundColor: STATUS_PILL[playbook.status]?.bg, color: STATUS_PILL[playbook.status]?.color }}>
              {STATUS_PILL[playbook.status]?.label}
            </span>
          </div>
        </div>
        <div className="mt-3 h-2 rounded-full bg-[#EEEEF5] overflow-hidden">
          <div className="h-full rounded-full" style={{ width: `${playbook.progress || 0}%`, backgroundColor: COLORS.blue }} />
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-[rgba(0,0,0,0.08)] mb-5 overflow-x-auto">
        {tabs.map((t) => {
          const isActive = tab === t.key;
          const completed = playbook[t.key]?.completed;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className="px-5 py-3 text-[14px] font-semibold border-b-2 -mb-[1px] flex items-center gap-2 whitespace-nowrap transition-colors"
              style={{
                borderColor: isActive ? COLORS.blue : "transparent",
                color: isActive ? COLORS.blue : COLORS.text2,
              }}
              data-testid={`playbook-tab-${t.key}`}
            >
              {t.label}
              {completed && <Check className="h-3.5 w-3.5" style={{ color: COLORS.green }} />}
            </button>
          );
        })}
      </div>

      <div className="rounded-xl bg-white border border-[rgba(0,0,0,0.08)] p-6">
        {tab === "trigger" && <TriggerTab playbook={playbook} update={update} />}
        {tab === "inputs" && <InputsTab playbook={playbook} update={update} />}
        {tab === "steps" && <StepsTab playbook={playbook} update={update} />}
        {tab === "outputs" && <OutputsTab playbook={playbook} update={update} setAiTool={setAiTool} tenantId={tenantId} />}
      </div>

      {/* Footer save bar */}
      <div className="mt-4 flex items-center justify-between gap-3 flex-wrap text-[12px]">
        <span className="text-[#9CA3AF]">
          {saving ? "Saving…" : lastSaved ? `Auto-saved · ${Math.floor((Date.now() - lastSaved) / 60000)} min ago` : "Not saved yet"}
          {dirty && <span className="ml-2 text-[#D4860A]">• Unsaved changes</span>}
        </span>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => persist()}
            disabled={saving}
            className="px-4 h-9 rounded-lg text-[13px] font-semibold text-[#4B5563] border border-[rgba(0,0,0,0.08)] hover:bg-[#f3f4f6]"
            data-testid="playbook-save-btn"
          >
            Save Draft
          </button>
          <button
            type="button"
            onClick={() => persist(tab)}
            disabled={saving}
            className="px-4 h-9 rounded-lg text-[13px] font-semibold text-white"
            style={{ backgroundColor: COLORS.blue }}
            data-testid="playbook-save-continue-btn"
          >
            Save & Continue →
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Trigger Tab ───
const TriggerTab = ({ playbook, update }) => {
  const t = playbook.trigger || {};
  return (
    <div className="space-y-4" data-testid="trigger-tab">
      <div>
        <label className="block text-[13px] font-medium text-[#374151] mb-1">What starts this workflow *</label>
        <textarea
          value={t.description || ""}
          onChange={(e) => update("trigger", { description: e.target.value })}
          rows={2}
          className="w-full rounded-lg border border-[rgba(0,0,0,0.15)] bg-white text-[14px] px-3 py-2 focus:border-[#3B4FE4] focus:outline-none resize-none"
          data-testid="trigger-description"
        />
      </div>
      <div>
        <label className="block text-[13px] font-medium text-[#374151] mb-2">Trigger source</label>
        <div className="flex flex-wrap gap-2">
          {TRIGGER_SOURCES.map((src) => {
            const sel = (t.sources || []).includes(src);
            return (
              <button
                key={src}
                type="button"
                onClick={() => {
                  const cur = t.sources || [];
                  update("trigger", { sources: sel ? cur.filter((x) => x !== src) : [...cur, src] });
                }}
                className="px-3 py-1.5 rounded-full text-[12px] font-semibold transition-colors"
                style={
                  sel
                    ? { backgroundColor: COLORS.blue, color: "white" }
                    : { backgroundColor: COLORS.bg3, color: COLORS.text2 }
                }
                data-testid={`trigger-source-${src.replace(/\s+/g, "-").toLowerCase()}`}
              >
                {src}
              </button>
            );
          })}
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-[13px] font-medium text-[#374151] mb-1">Estimated daily volume</label>
          <input
            type="number"
            min="0"
            value={t.daily_volume || 0}
            onChange={(e) => update("trigger", { daily_volume: parseInt(e.target.value, 10) || 0 })}
            className="w-full h-10 rounded-lg border border-[rgba(0,0,0,0.15)] bg-white text-[14px] px-3 focus:border-[#3B4FE4] focus:outline-none"
          />
        </div>
        <div>
          <label className="block text-[13px] font-medium text-[#374151] mb-1">Business hours only?</label>
          <button
            type="button"
            onClick={() => update("trigger", { business_hours_only: !t.business_hours_only })}
            className="w-12 h-6 rounded-full transition-colors relative"
            style={{ backgroundColor: t.business_hours_only ? COLORS.blue : "#d1d5db" }}
          >
            <span
              className="absolute top-1 h-4 w-4 rounded-full bg-white transition-all"
              style={{ left: t.business_hours_only ? "26px" : "4px" }}
            />
          </button>
        </div>
      </div>
      <div>
        <label className="block text-[13px] font-medium text-[#374151] mb-1">Notes / edge cases</label>
        <textarea
          value={t.notes || ""}
          onChange={(e) => update("trigger", { notes: e.target.value })}
          rows={2}
          className="w-full rounded-lg border border-[rgba(0,0,0,0.15)] bg-white text-[14px] px-3 py-2 focus:border-[#3B4FE4] focus:outline-none resize-none"
        />
      </div>
    </div>
  );
};

// ─── Inputs Tab ───
const InputsTab = ({ playbook, update }) => {
  const inp = playbook.inputs || {};
  const integrations = inp.integrations || [];
  const rr = inp.routing_rules || {};
  return (
    <div className="space-y-6" data-testid="inputs-tab">
      <div>
        <div className="text-[15px] font-semibold text-[#111827] mb-3">RAG Document Sources</div>
        <p className="text-[12px] text-[#9CA3AF] mb-3">Upload via Customer Portal Assets section. Tag here.</p>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-[13px] font-medium text-[#374151] mb-1">External enrichment source</label>
            <select
              value={inp.enrichment_source || ""}
              onChange={(e) => update("inputs", { enrichment_source: e.target.value })}
              className="w-full h-10 rounded-lg border border-[rgba(0,0,0,0.15)] bg-white text-[14px] px-2"
            >
              <option value="">Select…</option>
              {ENRICH_SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[13px] font-medium text-[#374151] mb-1">CRM as live RAG source</label>
            <select
              value={inp.crm_source || ""}
              onChange={(e) => update("inputs", { crm_source: e.target.value })}
              className="w-full h-10 rounded-lg border border-[rgba(0,0,0,0.15)] bg-white text-[14px] px-2"
            >
              <option value="">Select…</option>
              {CRM_SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>
      </div>

      <div>
        <div className="text-[15px] font-semibold text-[#111827] mb-3">System Integrations</div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {integrations.map((it, idx) => (
            <button
              key={it.id || it.name}
              type="button"
              onClick={() => {
                const next = [...integrations];
                next[idx] = { ...it, enabled: !it.enabled };
                update("inputs", { integrations: next });
              }}
              className="text-left rounded-lg border p-3 transition-colors"
              style={{
                borderColor: it.enabled ? COLORS.blue : COLORS.border,
                backgroundColor: it.enabled ? COLORS.blueLight : "white",
                borderWidth: it.enabled ? "2px" : "1px",
              }}
              data-testid={`integration-${it.id || it.name}`}
            >
              <div className="text-[13px] font-bold text-[#111827]">{it.name}</div>
              <div className="text-[11px] text-[#9CA3AF] mt-0.5">{it.role}</div>
              {it.enabled && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {(it.options || []).map((opt) => {
                    const checked = (it.write_permissions || []).includes(opt);
                    return (
                      <span
                        key={opt}
                        onClick={(e) => {
                          e.stopPropagation();
                          const next = [...integrations];
                          const cur = it.write_permissions || [];
                          next[idx] = { ...it, write_permissions: checked ? cur.filter((x) => x !== opt) : [...cur, opt] };
                          update("inputs", { integrations: next });
                        }}
                        className="px-2 py-0.5 text-[10px] font-semibold rounded-full cursor-pointer"
                        style={
                          checked
                            ? { backgroundColor: COLORS.blue, color: "white" }
                            : { backgroundColor: "white", color: COLORS.text2, border: "1px solid rgba(0,0,0,0.15)" }
                        }
                      >
                        {opt}
                      </span>
                    );
                  })}
                </div>
              )}
            </button>
          ))}
        </div>
      </div>

      <div>
        <div className="text-[15px] font-semibold text-[#111827] mb-3">Routing Rules</div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-[13px] font-medium text-[#374151] mb-1">High-priority signal keywords</label>
            <input
              value={rr.priority_keywords || ""}
              onChange={(e) => update("inputs", { routing_rules: { ...rr, priority_keywords: e.target.value } })}
              placeholder="urgent, executive, demo…"
              className="w-full h-10 rounded-lg border border-[rgba(0,0,0,0.15)] bg-white text-[14px] px-3"
            />
          </div>
          <div>
            <label className="block text-[13px] font-medium text-[#374151] mb-1">Target company size</label>
            <input
              value={rr.company_size || ""}
              onChange={(e) => update("inputs", { routing_rules: { ...rr, company_size: e.target.value } })}
              placeholder="50–500 employees"
              className="w-full h-10 rounded-lg border border-[rgba(0,0,0,0.15)] bg-white text-[14px] px-3"
            />
          </div>
          <div>
            <label className="block text-[13px] font-medium text-[#374151] mb-1">Target industries</label>
            <input
              value={rr.industries || ""}
              onChange={(e) => update("inputs", { routing_rules: { ...rr, industries: e.target.value } })}
              placeholder="SaaS, Fintech, Healthcare"
              className="w-full h-10 rounded-lg border border-[rgba(0,0,0,0.15)] bg-white text-[14px] px-3"
            />
          </div>
          <div>
            <label className="block text-[13px] font-medium text-[#374151] mb-1">Route hot leads to</label>
            <select
              value={rr.route_to || ""}
              onChange={(e) => update("inputs", { routing_rules: { ...rr, route_to: e.target.value } })}
              className="w-full h-10 rounded-lg border border-[rgba(0,0,0,0.15)] bg-white text-[14px] px-2"
            >
              <option value="">Select…</option>
              {ROUTE_TO_OPTIONS.map((o) => <option key={o}>{o}</option>)}
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className="block text-[13px] font-medium text-[#374151] mb-1">Auto-response SLA target</label>
            <select
              value={rr.sla_minutes || ""}
              onChange={(e) => update("inputs", { routing_rules: { ...rr, sla_minutes: parseInt(e.target.value, 10) || 0 } })}
              className="w-full h-10 rounded-lg border border-[rgba(0,0,0,0.15)] bg-white text-[14px] px-2"
            >
              <option value="">Select…</option>
              {SLA_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Steps Tab ───
const StepsTab = ({ playbook, update }) => {
  const s = playbook.steps || {};
  const steps = s.workflow_steps || [];
  const g = s.guardrails || {};

  const updateStep = (idx, patch) => {
    const next = [...steps];
    next[idx] = { ...next[idx], ...patch };
    update("steps", { workflow_steps: next });
  };
  const removeStep = (idx) => {
    const next = steps.filter((_, i) => i !== idx).map((s, i) => ({ ...s, order: i + 1 }));
    update("steps", { workflow_steps: next });
  };
  const addStep = () => {
    update("steps", { workflow_steps: [...steps, { order: steps.length + 1, description: "", type: "AI automated" }] });
  };

  return (
    <div className="space-y-6" data-testid="steps-tab">
      <div>
        <div className="text-[15px] font-semibold text-[#111827] mb-3">End-to-end Workflow Steps</div>
        <div className="space-y-2">
          {steps.map((st, idx) => (
            <div key={idx} className="flex items-center gap-2 rounded-lg border border-[rgba(0,0,0,0.08)] bg-white p-2" data-testid={`step-row-${idx}`}>
              <GripVertical className="h-4 w-4 text-[#9CA3AF] cursor-grab" />
              <span className="w-7 h-7 rounded-full flex items-center justify-center text-[12px] font-bold shrink-0" style={{ backgroundColor: COLORS.blueLight, color: COLORS.blue }}>{idx + 1}</span>
              <input
                value={st.description || ""}
                onChange={(e) => updateStep(idx, { description: e.target.value })}
                className="flex-1 h-9 rounded border border-[rgba(0,0,0,0.10)] bg-white text-[13px] px-2"
              />
              <select
                value={st.type || "AI automated"}
                onChange={(e) => updateStep(idx, { type: e.target.value })}
                className="h-9 rounded border border-[rgba(0,0,0,0.10)] bg-white text-[12px] px-2"
              >
                <option>AI automated</option>
                <option>Human review</option>
                <option>AI + human</option>
              </select>
              <button type="button" onClick={() => removeStep(idx)} className="h-9 w-9 rounded text-[#9CA3AF] hover:bg-[#fef2f2] hover:text-[#dc2626] flex items-center justify-center">
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
        <button
          type="button"
          onClick={addStep}
          className="mt-2 inline-flex items-center gap-1.5 px-3 h-9 rounded-lg text-[13px] font-semibold text-[#3B4FE4] border border-dashed border-[#3B4FE4] hover:bg-[#fafbff]"
          data-testid="add-step-btn"
        >
          <Plus className="h-3.5 w-3.5" /> Add step
        </button>
      </div>

      <div>
        <div className="text-[15px] font-semibold text-[#111827] mb-3">Guardrails & Human Review Rules</div>
        <div className="mb-3">
          <label className="block text-[13px] font-medium text-[#374151] mb-2">Always require human review before</label>
          <div className="flex flex-wrap gap-2">
            {REVIEW_OPTIONS.map((opt) => {
              const sel = (g.require_review_before || []).includes(opt);
              return (
                <button
                  key={opt}
                  type="button"
                  onClick={() => {
                    const cur = g.require_review_before || [];
                    update("steps", { guardrails: { ...g, require_review_before: sel ? cur.filter((x) => x !== opt) : [...cur, opt] } });
                  }}
                  className="px-3 py-1.5 rounded-full text-[12px] font-semibold"
                  style={sel ? { backgroundColor: COLORS.blue, color: "white" } : { backgroundColor: COLORS.bg3, color: COLORS.text2 }}
                >
                  {opt}
                </button>
              );
            })}
          </div>
        </div>
        <div className="mb-3">
          <label className="block text-[13px] font-medium text-[#374151] mb-2">Escalate to human when</label>
          <div className="flex flex-wrap gap-2">
            {ESCALATE_OPTIONS.map((opt) => {
              const sel = (g.escalate_on || []).includes(opt);
              return (
                <button
                  key={opt}
                  type="button"
                  onClick={() => {
                    const cur = g.escalate_on || [];
                    update("steps", { guardrails: { ...g, escalate_on: sel ? cur.filter((x) => x !== opt) : [...cur, opt] } });
                  }}
                  className="px-3 py-1.5 rounded-full text-[12px] font-semibold"
                  style={sel ? { backgroundColor: COLORS.blue, color: "white" } : { backgroundColor: COLORS.bg3, color: COLORS.text2 }}
                >
                  {opt}
                </button>
              );
            })}
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-[13px] font-medium text-[#374151] mb-1">Escalate to</label>
            <input
              value={g.escalate_to || ""}
              onChange={(e) => update("steps", { guardrails: { ...g, escalate_to: e.target.value } })}
              placeholder="Sales Manager / Account Owner"
              className="w-full h-10 rounded-lg border border-[rgba(0,0,0,0.15)] bg-white text-[14px] px-3"
            />
          </div>
          <div>
            <label className="block text-[13px] font-medium text-[#374151] mb-1">Max autonomous follow-up attempts</label>
            <select
              value={g.max_auto_followups ?? 3}
              onChange={(e) => update("steps", { guardrails: { ...g, max_auto_followups: parseInt(e.target.value, 10) } })}
              className="w-full h-10 rounded-lg border border-[rgba(0,0,0,0.15)] bg-white text-[14px] px-2"
            >
              {[1, 2, 3, 5, 10].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Outputs Tab ───
const OutputsTab = ({ playbook, update, setAiTool, tenantId }) => {
  const op = playbook.outputs || {};
  const outputs = op.expected_outputs || [];
  const kpis = op.kpis || [];
  const [yamlCopied, setYamlCopied] = useState(false);

  const updateOutput = (idx, patch) => {
    const next = [...outputs];
    next[idx] = { ...next[idx], ...patch };
    update("outputs", { expected_outputs: next });
  };
  const updateKpi = (idx, patch) => {
    const next = [...kpis];
    next[idx] = { ...next[idx], ...patch };
    update("outputs", { kpis: next });
  };

  const handleRegenerate = async () => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/playbooks/${playbook.id}/regenerate-config`, {
        method: "POST",
        headers: headers(tenantId),
      });
      const data = await r.json();
      update("outputs", {});
      // Force reload by mutating playbook locally
      playbook.generated_config_yaml = data.yaml;
      playbook.config_generated_at = data.generated_at;
      toast.success("YAML regenerated");
    } catch {
      toast.error("Could not regenerate config");
    }
  };

  const yaml = playbook.generated_config_yaml || "// Complete all 4 sections to generate the agent config";

  const copyYaml = () => {
    navigator.clipboard.writeText(yaml).then(() => {
      setYamlCopied(true);
      setTimeout(() => setYamlCopied(false), 2000);
      toast.success("Copied YAML to clipboard");
    });
  };

  const exportYaml = () => {
    const blob = new Blob([yaml], { type: "text/yaml" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `workflow_${playbook.workflow_id}_${(playbook.workflow_name || "playbook").replace(/[^a-zA-Z0-9]/g, "_")}.yaml`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const launchAgent = () => {
    const tool = playbook.ai_tool || "claude_cowork";
    copyYaml();
    if (tool === "claude_cowork") {
      window.open(`cowork://import?config=playbook_${playbook.id}`, "_blank");
      toast.message("Opened Claude Cowork. Paste the YAML in the new agent.");
    } else if (tool === "openclaw") {
      toast.message("YAML copied — paste into your OpenClaw instance.");
    } else {
      exportYaml();
      toast.message("YAML downloaded — load into your Perplexity API setup.");
    }
  };

  return (
    <div className="space-y-6" data-testid="outputs-tab">
      <div>
        <div className="text-[15px] font-semibold text-[#111827] mb-3">Expected Outputs</div>
        <div className="space-y-3">
          {outputs.map((o, idx) => (
            <div key={idx} className="rounded-lg border border-[rgba(0,0,0,0.08)] bg-white p-4">
              <input
                value={o.name || ""}
                onChange={(e) => updateOutput(idx, { name: e.target.value })}
                placeholder="Output name"
                className="w-full text-[14px] font-semibold text-[#111827] mb-2 focus:outline-none"
              />
              <textarea
                value={o.description || ""}
                onChange={(e) => updateOutput(idx, { description: e.target.value })}
                rows={2}
                className="w-full text-[13px] text-[#4B5563] focus:outline-none resize-none"
              />
            </div>
          ))}
        </div>
        <textarea
          value={op.additional_notes || ""}
          onChange={(e) => update("outputs", { additional_notes: e.target.value })}
          placeholder="Any additional outputs?"
          rows={2}
          className="mt-3 w-full rounded-lg border border-[rgba(0,0,0,0.15)] bg-white text-[14px] px-3 py-2 focus:border-[#3B4FE4] focus:outline-none resize-none"
        />
      </div>

      <div>
        <div className="text-[15px] font-semibold text-[#111827] mb-3">Success Metrics (KPIs)</div>
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="text-left text-[#9CA3AF] uppercase tracking-[0.06em] text-[10px] border-b border-[rgba(0,0,0,0.08)]">
                <th className="py-2 pr-3">KPI</th>
                <th className="py-2 pr-3">Baseline</th>
                <th className="py-2 pr-3">Target</th>
                <th className="py-2">Unit</th>
              </tr>
            </thead>
            <tbody>
              {kpis.map((k, idx) => (
                <tr key={idx} className="border-b border-[rgba(0,0,0,0.05)]">
                  <td className="py-2 pr-3"><input value={k.name || ""} onChange={(e) => updateKpi(idx, { name: e.target.value })} className="w-full bg-transparent focus:outline-none" /></td>
                  <td className="py-2 pr-3"><input value={k.baseline || ""} onChange={(e) => updateKpi(idx, { baseline: e.target.value })} className="w-full bg-[#fafbff] rounded px-2 py-1 focus:outline-none" /></td>
                  <td className="py-2 pr-3"><input value={k.target || ""} onChange={(e) => updateKpi(idx, { target: e.target.value })} className="w-full bg-[#fafbff] rounded px-2 py-1 focus:outline-none" /></td>
                  <td className="py-2"><input value={k.unit || ""} onChange={(e) => updateKpi(idx, { unit: e.target.value })} className="w-full bg-transparent focus:outline-none" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div>
        <div className="text-[15px] font-semibold text-[#111827] mb-3">AI Execution Tool</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {AI_TOOLS.map((t) => {
            const sel = playbook.ai_tool === t.id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setAiTool(t.id)}
                className="text-left rounded-lg border p-4 transition-all"
                style={{ borderColor: sel ? COLORS.blue : COLORS.border, borderWidth: sel ? "2px" : "1px", backgroundColor: sel ? COLORS.blueLight : "white" }}
                data-testid={`ai-tool-${t.id}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[14px] font-bold text-[#111827]">{t.label}</div>
                  <span className="px-2 py-0.5 text-[10px] font-bold rounded-full" style={{ backgroundColor: sel ? "white" : COLORS.bg3, color: sel ? COLORS.blue : COLORS.text2 }}>
                    {t.badge}
                  </span>
                </div>
                <p className="text-[12px] text-[#4B5563] leading-snug">{t.description}</p>
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="text-[15px] font-semibold text-[#111827]">Generated Agent Config</div>
          <button type="button" onClick={handleRegenerate} className="text-[12px] font-semibold text-[#3B4FE4]">Regenerate</button>
        </div>
        <pre className="rounded-lg p-4 text-[12px] text-[#a3e7ff] overflow-x-auto" style={{ backgroundColor: "#0B1628", maxHeight: "320px" }} data-testid="yaml-config">
{yaml}
        </pre>
        <div className="mt-3 flex flex-wrap gap-2">
          <button type="button" onClick={copyYaml} className="inline-flex items-center gap-1.5 px-4 h-9 rounded-lg text-[13px] font-semibold text-white" style={{ backgroundColor: COLORS.blue }} data-testid="copy-yaml-btn">
            <Copy className="h-3.5 w-3.5" /> {yamlCopied ? "Copied!" : "Copy YAML"}
          </button>
          <button type="button" onClick={exportYaml} className="inline-flex items-center gap-1.5 px-4 h-9 rounded-lg text-[13px] font-semibold border border-[rgba(0,0,0,0.15)] text-[#4B5563] hover:bg-[#f9fafb]" data-testid="export-yaml-btn">
            <Download className="h-3.5 w-3.5" /> Export Config
          </button>
          <button type="button" onClick={launchAgent} className="inline-flex items-center gap-1.5 px-4 h-9 rounded-lg text-[13px] font-semibold text-[#3B4FE4] border border-[#3B4FE4] hover:bg-[#fafbff]" data-testid="launch-agent-btn">
            <ExternalLink className="h-3.5 w-3.5" /> Launch Agent
          </button>
        </div>
      </div>
    </div>
  );
};

// ──── Deep-link wrapper: reads /portal/:tenantId/playbooks/:id and sets state on parent ────
export function PlaybookDeepLinkBootstrap({ onResolve }) {
  const { tenantId, playbookId } = useParams();
  useEffect(() => {
    if (onResolve) onResolve(tenantId, playbookId);
  }, [tenantId, playbookId, onResolve]);
  return null;
}
